// src/components/SceneLayers/GroupHighlights/GroupHighlights.tsx
import React from 'react';
import { Box } from '@mui/material';
import { Lock } from '@mui/icons-material';
import { useUiStateStore } from '@stores/uiStateStore';
import { useGroupStore } from '@stores/groupStore';
import { useScene } from '@hooks/useScene';
import { useIsoProjection } from '@hooks/useIsoProjection';
import { GroupableElement } from '@/features/groups/groupTypes';
import { Svg } from '@/components/Svg/Svg';
import { SceneLayer } from '@/components/SceneLayer/SceneLayer';
import { RenderLayer } from '@/core/renderer/types';
import { Coords } from '@/types';

export const GroupHighlights: React.FC = () => {
  const itemControls = useUiStateStore((state) => state.itemControls);
  const dropTargetGroupId = useUiStateStore((state) => state.dropTargetGroupId);
  const groups = useGroupStore((state) => state.groups);
  const groupActions = useGroupStore((state) => state.actions);
  const scene = useScene();

  // Get currently selected elements
  const getSelectedElements = (): GroupableElement[] => {
    if (!itemControls) return [];

    if (itemControls.type === 'MULTI_SELECT') {
      return itemControls.items.map(item => ({
        type: item.type as any,
        id: item.id
      }));
    } else if (itemControls.type === 'ADD_ITEM') {
      return [];
    } else if ('id' in itemControls) {
      return [{
        type: itemControls.type as any,
        id: itemControls.id
      }];
    }
    return [];
  };

  const selectedElements = getSelectedElements();

  // Find groups that contain selected elements
  const activeGroupIds = React.useMemo(() => {
    const ids = new Set<string>();
    selectedElements.forEach(el => {
      const group = groupActions.getGroupForElement(el);
      if (group) {
        ids.add(group.id);
      }
    });
    return ids;
  }, [selectedElements, groups, groupActions]);

  // Calculate bounding box for all elements in a group
  const getGroupBoundingBox = (elements: GroupableElement[]) => {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    elements.forEach(element => {
      let item: any = null;
      let fromX = 0;
      let fromY = 0;
      let toX = 0;
      let toY = 0;

      if (element.type === 'ITEM') {
        item = scene.items.find(i => i.id === element.id);
        if (item) {
          fromX = item.tile.x;
          fromY = item.tile.y;
          // Tile bounds are inclusive in useIsoProjection. An icon occupies
          // exactly its anchor tile, so adding one here creates a false extra
          // row and column around the group.
          toX = item.tile.x;
          toY = item.tile.y;
        }
      } else if (element.type === 'RECTANGLE') {
        item = scene.rectangles.find(r => r.id === element.id);
        if (item) {
          fromX = Math.min(item.from.x, item.to.x);
          fromY = Math.min(item.from.y, item.to.y);
          toX = Math.max(item.from.x, item.to.x);
          toY = Math.max(item.from.y, item.to.y);
        }
      } else if (element.type === 'TEXTBOX') {
        item = scene.textBoxes.find(t => t.id === element.id);
        if (item) {
          fromX = item.tile.x;
          fromY = item.tile.y;
          const width = item.size?.width || 2;
          toX = item.tile.x + width;
          toY = item.tile.y + 1;
        }
      } else if (element.type === 'CONNECTOR') {
        item = scene.connectors.find(c => c.id === element.id);
        if (item && item.path && item.path.rectangle) {
          // Use the connector's pre-calculated rectangle bounds
          fromX = Math.min(item.path.rectangle.from.x, item.path.rectangle.to.x);
          fromY = Math.min(item.path.rectangle.from.y, item.path.rectangle.to.y);
          toX = Math.max(item.path.rectangle.from.x, item.path.rectangle.to.x);
          toY = Math.max(item.path.rectangle.from.y, item.path.rectangle.to.y);
        }
      }

      if (item) {
        minX = Math.min(minX, fromX);
        maxX = Math.max(maxX, toX);
        minY = Math.min(minY, fromY);
        maxY = Math.max(maxY, toY);
      }
    });

    if (minX === Infinity) return null;

    return {
      from: { x: minX, y: minY },
      to: { x: maxX, y: maxY }
    };
  };

  // Also render drop target group if not already active
  const groupIdsToRender = new Set(activeGroupIds);
  if (dropTargetGroupId && !groupIdsToRender.has(dropTargetGroupId)) {
    groupIdsToRender.add(dropTargetGroupId);
  }

  return (
    <SceneLayer order={RenderLayer.NODES + 0.5}>
      {Array.from(groupIdsToRender).map(groupId => {
        const group = groups.find(g => g.id === groupId);
        if (!group) return null;

        const elements = groupActions.getGroupElements(groupId);
        const bbox = getGroupBoundingBox(elements);

        if (!bbox) return null;

        const isDropTarget = groupId === dropTargetGroupId;

        return (
          <GroupHighlight
            key={`group-highlight-${groupId}`}
            from={bbox.from}
            to={bbox.to}
            groupName={group.name}
            groupColor={group.color || '#8b5cf6'}
            isLocked={group.locked}
            isDropTarget={isDropTarget}
          />
        );
      })}
    </SceneLayer>
  );
};

// Separate component for each group highlight with isometric projection
interface GroupHighlightProps {
  from: Coords;
  to: Coords;
  groupName: string;
  groupColor: string;
  isLocked: boolean;
  isDropTarget: boolean;
}

const GroupHighlight: React.FC<GroupHighlightProps> = ({ from, to, groupName, groupColor, isLocked, isDropTarget }) => {
  const { css, pxSize } = useIsoProjection({ from, to });

  const strokeWidth = 4;
  const padding = 16;

  // Drop target gets special green color and thicker border
  const displayColor = isDropTarget ? '#22c55e' : groupColor;
  const displayStrokeWidth = isDropTarget ? strokeWidth + 2 : (isLocked ? strokeWidth + 1 : strokeWidth);
  const displayOpacity = isDropTarget ? 1 : (isLocked ? 1 : 0.9);

  return (
    <>
      {/* Isometric selection box */}
      <Svg
        style={{
          ...css,
          pointerEvents: 'none',
          filter: isDropTarget
            ? `drop-shadow(0 0 20px ${displayColor}80)`
            : `drop-shadow(0 0 12px ${displayColor}60)`
        }}
      >
        <g>
          {/* Animated pulsing rectangle - solid when locked or drop target, dashed when unlocked */}
          <rect
            x={-padding}
            y={-padding}
            width={pxSize.width + padding * 2}
            height={pxSize.height + padding * 2}
            fill="none"
            stroke={displayColor}
            strokeWidth={displayStrokeWidth}
            strokeDasharray={(isLocked || isDropTarget) ? 'none' : `${strokeWidth * 3} ${strokeWidth * 2}`}
            strokeLinecap="round"
            rx={8}
            opacity={displayOpacity}
          >
            {!isLocked && !isDropTarget && (
              <>
                <animate
                  attributeName="stroke-dashoffset"
                  from="0"
                  to={strokeWidth * 5}
                  dur="1s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="0.7;1;0.7"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </>
            )}
            {isDropTarget && (
              <>
                <animate
                  attributeName="opacity"
                  values="0.8;1;0.8"
                  dur="0.8s"
                  repeatCount="indefinite"
                />
              </>
            )}
          </rect>

          {/* Solid background for visibility */}
          <rect
            x={-padding}
            y={-padding}
            width={pxSize.width + padding * 2}
            height={pxSize.height + padding * 2}
            fill={displayColor}
            opacity={isDropTarget ? 0.12 : 0.05}
            rx={8}
          />
        </g>
      </Svg>

      {/* Group name badge - rotated to align with isometric edge */}
      <Box
        sx={{
          position: 'absolute',
          px: 1.5,
          py: 0.5,
          bgcolor: displayColor,
          color: 'white',
          fontSize: '0.75rem',
          fontWeight: 600,
          borderRadius: '4px',
          boxShadow: `0 2px 8px ${displayColor}60`,
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          zIndex: 1,
          transform: 'rotate(-30deg)',
          transformOrigin: 'left center',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
        style={{
          left: (css.left as number) - 12,
          top: (css.top as number) - 20
        }}
      >
        {isLocked && <Lock sx={{ fontSize: 12 }} />}
        {isDropTarget ? `+ Add to ${groupName}` : groupName}
      </Box>
    </>
  );
};
